import { useMount, useUnmount, useUpdateEffect } from "ahooks";
import clsx from "clsx";
import { t } from "i18next";
import { FC, useRef, useState, useEffect } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

import VoiceIcon from "@/svg/VoiceIcon";
import { feedbackToast } from "@/utils/common";
import { getResourceUrl } from "@/utils/common";

import { IMessageItemProps } from ".";
import styles from "./message-item.module.scss";

// 全局唯一音频实例
const getGlobalAudio = () => {
  if (!(window as any).__globalVoiceAudio) {
    (window as any).__globalVoiceAudio = new Audio();
  }
  return (window as any).__globalVoiceAudio as HTMLAudioElement;
};

// 全局当前播放的语音ID
const setGlobalPlayingId = (id: string | null) => {
  (window as any).__globalVoicePlayingId = id;
  window.dispatchEvent(new CustomEvent("voice-playing-id-changed", { detail: id }));
};
const getGlobalPlayingId = (): string | null => {
  return (window as any).__globalVoicePlayingId || null;
};

// 全局 FFmpeg.wasm 实例
const getGlobalFFmpeg = (): FFmpeg => {
  if (!(window as any).__globalFFmpeg) {
    (window as any).__globalFFmpeg = new FFmpeg();
  }
  return (window as any).__globalFFmpeg;
};

// 初始化 FFmpeg.wasm
const initFFmpeg = async (): Promise<boolean> => {
  const ffmpeg = getGlobalFFmpeg();
  
  if (ffmpeg.loaded) {
    return true;
  }

  try {
    // 使用 CDN 加载 FFmpeg.wasm 核心文件
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    return true;
  } catch (error) {
    console.error('FFmpeg.wasm 初始化失败:', error);
    return false;
  }
};

const VoiceMessageRender: FC<IMessageItemProps> = ({ message, isSender, disabled }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [isUserInitiated, setIsUserInitiated] = useState(false);
  const [globalPlayingId, setGlobalPlayingIdState] = useState<string | null>(getGlobalPlayingId());
  const [isConverting, setIsConverting] = useState(false);

  // 只有当前气泡的ID等于全局播放ID时，才显示播放动画
  const isPlaying = globalPlayingId === message.clientMsgID;

  // 清理 blob
  const cleanup = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setHasError(false);
    setIsUserInitiated(false);
  };

  useUnmount(() => {
    setIsComponentMounted(false);
    cleanup();
  });

  // 使用 FFmpeg 转换音频
  const convertAudioWithFFmpeg = async (originalBlob: Blob, filename: string, retryCount = 0) => {
    let conversionSuccess = false;
    
    try {
      setIsConverting(true);
      
      // 检查是否在 Electron 环境中
      if ((window as any).electronAPI?.convertAudioToMp3) {
        // Electron 环境：使用原生 FFmpeg
        const arrayBuffer = await originalBlob.arrayBuffer();
        const result = await (window as any).electronAPI.convertAudioToMp3(arrayBuffer, filename);
        
        if (result?.success && result.buffer) {
          const mp3Buffer = new Uint8Array(result.buffer);
          const mp3Blob = new Blob([mp3Buffer], { type: 'audio/mpeg' });
          const mp3ObjectUrl = URL.createObjectURL(mp3Blob);
          setBlobUrl(mp3ObjectUrl);
          conversionSuccess = true;
          return true;
        } else {
          throw new Error(result?.error || '音频转换失败');
        }
      } else {
        // Web 环境：使用 FFmpeg.wasm
        const isFFmpegReady = await initFFmpeg();
        if (!isFFmpegReady) {
          throw new Error('FFmpeg 初始化失败');
        }

        const ffmpeg = getGlobalFFmpeg();
        const inputName = `input_${Date.now()}_${retryCount}.m4a`;
        const outputName = `output_${Date.now()}_${retryCount}.mp3`;

        try {
          // 清理可能存在的同名文件
          try {
            await ffmpeg.deleteFile(inputName);
          } catch (e) { /* 忽略，文件可能不存在 */ }
          try {
            await ffmpeg.deleteFile(outputName);
          } catch (e) { /* 忽略，文件可能不存在 */ }

          // 写入输入文件
          await ffmpeg.writeFile(inputName, await fetchFile(originalBlob));

          // 执行转换
          await ffmpeg.exec([
            '-i', inputName,
            '-codec:a', 'libmp3lame',
            '-qscale:a', '2',
            outputName
          ]);

          // 检查输出文件是否存在
          let data;
          try {
            data = await ffmpeg.readFile(outputName);
          } catch (readError) {
            throw new Error('转换后的文件无法读取');
          }

          const mp3Blob = new Blob([data], { type: 'audio/mpeg' });
          const mp3ObjectUrl = URL.createObjectURL(mp3Blob);
          setBlobUrl(mp3ObjectUrl);
          conversionSuccess = true;

          // 立即返回成功，不等待清理
          return true;
        } catch (conversionError) {
          // 如果是 FS 错误且没有重试过，尝试重试
          if ((conversionError as any)?.message?.includes('FS error') && retryCount === 0) {
            // 等待一下再重试
            await new Promise(resolve => setTimeout(resolve, 200));
            return await convertAudioWithFFmpeg(originalBlob, filename, 1);
          }
          throw conversionError;
        } finally {
          // 异步清理文件，不影响返回结果
          setTimeout(async () => {
            try {
              await ffmpeg.deleteFile(inputName);
            } catch (e) { /* 忽略清理错误 */ }
            try {
              await ffmpeg.deleteFile(outputName);
            } catch (e) { /* 忽略清理错误 */ }
          }, 100);
        }
      }
    } catch (error) {
      console.error('转换失败:', error);
      
      // 只有在转换真正失败时才显示错误
      if (isComponentMounted && !conversionSuccess) {
        feedbackToast({ error, msg: '音频格式转换失败' });
      }
      return false;
    } finally {
      setIsConverting(false);
    }
  };

  // 加载音频
  const loadAudio = async () => {
    let url = message.soundElem!.sourceUrl;
    // let url = 'https://capp.hzkhy.com/api/object/76779845339615085931/msg_voice_72f6304a261879f5b05b1dc3b6b24341.m4a';

    url = getResourceUrl(url);
    setIsLoading(true);
    setHasError(false);
    cleanup();
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const blob = await res.blob();
      
      // 先尝试直接使用原始音频
      const objectUrl = URL.createObjectURL(blob);
      setBlobUrl(objectUrl);
      
      // 测试是否能播放，如果不能则转换
      const testAudio = new Audio(objectUrl);
      testAudio.onerror = async () => {
        // 播放失败，尝试转换
        const filename = url.split('/').pop() || 'audio_file';
        await convertAudioWithFFmpeg(blob, filename);
      };
    } catch (e) {
      setHasError(true);
      if (isComponentMounted) {
        feedbackToast({ error: e, msg: '音频加载失败' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useMount(() => {
    loadAudio();
  });

  useUpdateEffect(() => {
    loadAudio();
  }, [message.soundElem?.sourceUrl]);

  // 监听全局播放ID变化，驱动动画
  useEffect(() => {
    const handler = (e: any) => {
      setGlobalPlayingIdState(e.detail);
    };
    window.addEventListener("voice-playing-id-changed", handler);
    return () => window.removeEventListener("voice-playing-id-changed", handler);
  }, []);

  const playAudio = () => {
    if (isLoading || hasError || !blobUrl) {
      if (hasError) loadAudio();
      return;
    }
    const audio = getGlobalAudio();
    // 先暂停上一个
    audio.pause();
    audio.src = blobUrl;
    audio.currentTime = 0;
    setIsUserInitiated(true);
    setGlobalPlayingId(message.clientMsgID);
    audio.onended = () => setGlobalPlayingId(null);
    audio.onpause = () => setGlobalPlayingId(null);
    audio.onplay = () => setGlobalPlayingId(message.clientMsgID);
    audio.onerror = (e) => {
      setHasError(true);
      setGlobalPlayingId(null);
      if (isComponentMounted && isUserInitiated) {
        feedbackToast({ error: '音频播放失败', msg: '音频播放失败' });
      }
    };
    audio.play().catch(e => {
      if (isComponentMounted && isUserInitiated) {
        feedbackToast({ error: e, msg: t("toast.playAudioFailed") });
      }
    });
  };

  return (
    <div
      className={clsx(
        styles.bubble,
        "relative flex cursor-pointer items-center !py-2",
        !isSender && "flex-row-reverse",
        disabled && "justify-end"
      )}
      onClick={playAudio}
    >
      <VoiceIcon
        style={{ transform: isSender ? "rotateY(180deg)" : "none" }}
        playing={isPlaying}
      />
      <span className={isSender ? "mr-1" : "ml-1"}>
        {isConverting ? t('placeholder.loading') : `${message.soundElem!.duration} ''`}
      </span>
    </div>
  );
};

export default VoiceMessageRender;
